import React, { Suspense, lazy, ComponentType, Component, ErrorInfo, ReactNode } from 'react';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Loading fallback component for lazy-loaded routes
 */
export const RouteLoadingFallback: React.FC<{ message?: string }> = ({
  message = 'Loading...',
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      gap: 2,
    }}
  >
    <CircularProgress size={40} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

/**
 * Error fallback component for when lazy-loaded routes fail to load
 */
export const RouteErrorFallback: React.FC<{
  error?: Error;
  onRetry?: () => void;
}> = ({ error, onRetry }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      gap: 2,
      p: 3,
    }}
  >
    <Alert severity="error" sx={{ maxWidth: 400, width: '100%' }}>
      <Typography variant="body2" fontWeight={500} gutterBottom>
        Failed to load page
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {error?.message || 'An unexpected error occurred while loading this page.'}
      </Typography>
    </Alert>
    <Button
      variant="outlined"
      startIcon={<RefreshIcon />}
      onClick={onRetry || (() => window.location.reload())}
      sx={{ mt: 1 }}
    >
      Try Again
    </Button>
  </Box>
);

/**
 * Error boundary component for catching lazy load failures
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class LazyErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    console.error('Lazy load error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <RouteErrorFallback error={this.state.error} onRetry={this.handleRetry} />
        )
      );
    }

    return this.props.children;
  }
}

/**
 * Create a lazy-loaded component with Suspense wrapper and error boundary
 * @param importFn - Dynamic import function
 * @param fallbackMessage - Optional loading message
 */
export function lazyWithFallback<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  fallbackMessage?: string,
): React.FC<React.ComponentProps<T>> {
  const LazyComponent = lazy(importFn);

  return (props: React.ComponentProps<T>) => (
    <LazyErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback message={fallbackMessage} />}>
        <LazyComponent {...props} />
      </Suspense>
    </LazyErrorBoundary>
  );
}

// ============================================
// Lazy-loaded Admin Pages
// ============================================

export const LazyAdminDashboard = lazyWithFallback(
  () => import('../admin/pages/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
  'Loading dashboard...',
);

export const LazyUserManagement = lazyWithFallback(
  () => import('./Admin/UserManagementRedesigned'),
  'Loading user management...',
);

export const LazyGroupStoresManager = lazyWithFallback(
  () => import('./Admin/GroupStoresManager'),
  'Loading group stores...',
);

export const LazyPermissionsManager = lazyWithFallback(
  () => import('./Admin/PermissionsManager'),
  'Loading permissions...',
);

export const LazyClassificationLogs = lazyWithFallback(
  () => import('./Admin/ClassificationLogs'),
  'Loading classification logs...',
);

export const LazyAuditLog = lazyWithFallback(
  () => import('../admin/pages/AuditLog').then((m) => ({ default: m.AuditLog })),
  'Loading audit log...',
);

export const LazyCustomerEmailsAdmin = lazyWithFallback(
  () =>
    import('../admin/pages/CustomerEmailsAdmin').then((m) => ({
      default: m.CustomerEmailsAdmin,
    })),
  'Loading customer emails...',
);

export const LazyWarrantyExportsAdmin = lazyWithFallback(
  () =>
    import('../admin/pages/WarrantyExportsAdmin').then((m) => ({
      default: m.WarrantyExportsAdmin,
    })),
  'Loading warranty exports...',
);

export const LazyWarrantyContractsAdmin = lazyWithFallback(
  () =>
    import('../admin/pages/WarrantyContractsAdmin').then((m) => ({
      default: m.WarrantyContractsAdmin,
    })),
  'Loading warranty contracts...',
);

export const LazyDecoderPipeline = lazyWithFallback(
  () => import('../admin/pages/DecoderPipeline').then((m) => ({ default: m.DecoderPipeline })),
  'Loading decoder pipeline...',
);

export const LazyBIC = lazyWithFallback(
  () => import('./BIC').then((m) => ({ default: m.BIC })),
  'Loading BIC...',
);

// ============================================
// Lazy-loaded Report Pages
// ============================================

export const LazyReportsRedesigned = lazyWithFallback(
  () => import('./ReportsRedesigned').then((m) => ({ default: m.ReportsRedesigned })),
  'Loading reports...',
);

export const LazyOpenTrackMultiTenantReport = lazyWithFallback(
  () =>
    import('./OpenTrackMultiTenantReport').then((m) => ({
      default: m.OpenTrackMultiTenantReport,
    })),
  'Loading OpenTrack report...',
);

export const LazyDealervaultMultiTenantReport = lazyWithFallback(
  () =>
    import('./DealervaultMultiTenantReport').then((m) => ({
      default: m.DealervaultMultiTenantReport,
    })),
  'Loading Dealervault report...',
);

export const LazyChemicalsReport = lazyWithFallback(
  () =>
    import('./ChemicalsReport').then((m) => ({
      default: m.ChemicalsReport,
    })),
  'Loading Chemicals report...',
);

// ============================================
// Lazy-loaded Auth Pages
// ============================================

export const LazySetPassword = lazyWithFallback(
  () => import('./SetPassword').then((m) => ({ default: m.SetPassword })),
  'Loading...',
);

export const LazySetupMfa = lazyWithFallback(
  () => import('./SetupMfa').then((m) => ({ default: m.SetupMfa })),
  'Loading MFA setup...',
);

export const LazyAuthReset = lazyWithFallback(
  () => import('./AuthReset').then((m) => ({ default: m.AuthReset })),
  'Loading...',
);

export const LazyAuthMfa = lazyWithFallback(
  () => import('./AuthMfa').then((m) => ({ default: m.AuthMfa })),
  'Loading MFA challenge...',
);

// ============================================
// Lazy-loaded Insurance Partner Portal Pages
// ============================================

export const LazyInsurancePartnerShell = lazyWithFallback(
  () =>
    import('../partner/InsurancePartnerShell').then((m) => ({ default: m.InsurancePartnerShell })),
  'Loading partner portal...',
);

export const LazyPartnerDashboard = lazyWithFallback(
  () => import('../partner/pages/PartnerDashboard').then((m) => ({ default: m.PartnerDashboard })),
  'Loading dashboard...',
);

export const LazyPartnerReportDownload = lazyWithFallback(
  () =>
    import('../partner/pages/PartnerReportDownload').then((m) => ({
      default: m.PartnerReportDownload,
    })),
  'Loading reports...',
);

export const LazyAccountSettingsPage = lazyWithFallback(
  () =>
    import('../components/shared/AccountSettingsPage').then((m) => ({
      default: m.AccountSettingsPage,
    })),
  'Loading account settings...',
);

// ============================================
// Lazy-loaded Accounting Portal Pages
// ============================================

export const LazyAccountingShell = lazyWithFallback(
  () => import('../accounting/AccountingShell').then((m) => ({ default: m.AccountingShell })),
  'Loading accounting portal...',
);

export const LazyAccountingDashboard = lazyWithFallback(
  () =>
    import('../accounting/pages/AccountingDashboard').then((m) => ({
      default: m.AccountingDashboard,
    })),
  'Loading dashboard...',
);

export const LazyReportDownload = lazyWithFallback(
  () => import('../accounting/pages/ReportDownload').then((m) => ({ default: m.ReportDownload })),
  'Loading reports...',
);

export const LazyAccountCodesManagement = lazyWithFallback(
  () =>
    import('../accounting/pages/AccountCodesManagement').then((m) => ({
      default: m.AccountCodesManagement,
    })),
  'Loading account codes...',
);

export const LazyDownloadHistory = lazyWithFallback(
  () => import('../accounting/pages/DownloadHistory').then((m) => ({ default: m.DownloadHistory })),
  'Loading history...',
);

export const LazyAccountingSettings = lazyWithFallback(
  () =>
    import('../accounting/pages/AccountingSettings').then((m) => ({
      default: m.AccountingSettings,
    })),
  'Loading settings...',
);

export const LazyEmailSchedules = lazyWithFallback(
  () =>
    import('../accounting/pages/EmailSchedules').then((m) => ({
      default: m.EmailSchedules,
    })),
  'Loading email schedules...',
);

export const LazyDataAlerts = lazyWithFallback(
  () =>
    import('../accounting/pages/DataAlerts').then((m) => ({
      default: m.DataAlerts,
    })),
  'Loading alerts...',
);
